import { Injectable, QueryList, ElementRef } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class FocusManagementService {
  /**
   * Sets up keydown listeners for all focusable elements in a component
   * @param focusableElements QueryList of focusable elements
   * @param focusParentTabCallback Function to call when Tab is pressed on last element
   */
  setupKeydownListeners(
    focusableElements: QueryList<ElementRef>,
    focusParentTabCallback: () => void
  ): void {
    if (focusableElements) {
      focusableElements.forEach((element, index) => {
        element.nativeElement.addEventListener(
          'keydown',
          (event: KeyboardEvent) => {
            this.handleFocusableContentKeydown(
              event,
              index,
              focusableElements.length,
              focusParentTabCallback
            );
          }
        );
      });
    }
  }

  /**
   * Handles keydown events on focusable content elements
   * @param event The keyboard event
   * @param currentIndex Index of the current element
   * @param totalElements Total number of focusable elements
   * @param focusParentTabCallback Function to call when Tab is pressed on last element
   */
  private handleFocusableContentKeydown(
    event: KeyboardEvent,
    currentIndex: number,
    totalElements: number,
    focusParentTabCallback: () => void
  ): void {
    if (event.key === 'Tab' && !event.shiftKey) {
      const isLastElement = currentIndex === totalElements - 1;

      if (isLastElement) {
        event.preventDefault();
        if (focusParentTabCallback) {
          focusParentTabCallback();
        }
      }
    }
  }

  /**
   * Cleanup function to remove event listeners (optional, for components that need explicit cleanup)
   * @param focusableElements QueryList of focusable elements
   */
  cleanup(focusableElements: QueryList<ElementRef>): void {
    if (focusableElements) {
      focusableElements.forEach((element) => {
        // Clone node to remove all event listeners
        const newElement = element.nativeElement.cloneNode(true);
        element.nativeElement.parentNode?.replaceChild(
          newElement,
          element.nativeElement
        );
      });
    }
  }
}
